/**
 * File Storage Utilities
 * Handles file operations for the vault including copying, thumbnail generation, and deletion
 */

import type { PKMObject } from '../models/types';

// File size limits (in bytes)
export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB default
export const THUMBNAIL_SIZE = 200; // 200px for thumbnails

// Blocked file extensions (dangerous executable types)
const BLOCKED_EXTENSIONS = new Set([
  '.exe', '.dll', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif',
  '.sh', '.bash', '.csh', '.ksh',
  '.vbs', '.vbe', '.js', '.jse', '.wsf', '.wsh', '.ps1', '.psm1',
  '.app', '.action', '.command', '.workflow',
  '.reg', '.inf', '.hta', '.cpl',
]);

/**
 * Get the vault path from settings or default location
 */
export function getVaultPath(): string {
  // TODO: Get from settings
  // For now, use a default path
  return './vault';
}

/**
 * Get the files directory path within the vault
 */
export function getFilesDirectory(): string {
  return `${getVaultPath()}/files`;
}

/**
 * Get the directory path for a specific file object
 */
export function getFileObjectDirectory(objectId: string): string {
  return `${getFilesDirectory()}/${objectId}`;
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Get MIME type from file extension
 */
export function getMimeTypeFromExtension(extension: string): string {
  const mimeTypes: Record<string, string> = {
    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'txt': 'text/plain',
    'md': 'text/markdown',
    'csv': 'text/csv',

    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'bmp': 'image/bmp',
    'ico': 'image/x-icon',

    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',
    'm4a': 'audio/mp4',

    // Video
    'mp4': 'video/mp4',
    'webm': 'video/webm',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',

    // Archives
    'zip': 'application/zip',
    'rar': 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
    'tar': 'application/x-tar',
    'gz': 'application/gzip',

    // Code
    'js': 'text/javascript',
    'ts': 'text/typescript',
    'json': 'application/json',
    'html': 'text/html',
    'css': 'text/css',
    'py': 'text/x-python',
    'java': 'text/x-java',
    'cpp': 'text/x-c++src',
    'c': 'text/x-csrc',
  };

  return mimeTypes[extension.toLowerCase()] || 'application/octet-stream';
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Check if file type is an image
 */
export function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/**
 * Check if file type is a PDF
 */
export function isPDF(mimeType: string): boolean {
  return mimeType === 'application/pdf';
}

/**
 * Check if file type is a video
 */
export function isVideo(mimeType: string): boolean {
  return mimeType.startsWith('video/');
}

/**
 * Check if file type is audio
 */
export function isAudio(mimeType: string): boolean {
  return mimeType.startsWith('audio/');
}

/**
 * Check if file type supports OCR
 */
export function supportsOCR(mimeType: string): boolean {
  return isPDF(mimeType) || isImage(mimeType);
}

/**
 * Get icon emoji for file type
 */
export function getFileIcon(mimeType: string): string {
  if (isImage(mimeType)) return '🖼️';
  if (isPDF(mimeType)) return '📄';
  if (isVideo(mimeType)) return '🎥';
  if (isAudio(mimeType)) return '🎵';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📊';
  if (mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('compressed')) return '📦';
  if (mimeType.includes('text')) return '📃';
  if (mimeType.includes('json')) return '{ }';
  return '📎';
}

/**
 * Validate file before upload
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds maximum allowed size of ${formatFileSize(MAX_FILE_SIZE)}`
    };
  }

  // Check file name
  if (!file.name || file.name.trim() === '') {
    return {
      valid: false,
      error: 'Invalid file name'
    };
  }

  // Block dangerous file types
  const ext = ('.' + file.name.split('.').pop()?.toLowerCase()) || '';
  if (BLOCKED_EXTENSIONS.has(ext)) {
    return {
      valid: false,
      error: `File type "${ext}" is not allowed for security reasons`
    };
  }

  return { valid: true };
}

/**
 * Copy file to vault storage
 * Stores in both Supabase (for sync) and IndexedDB (for offline cache)
 */
export async function copyFileToVault(
  file: File,
  objectId: string
): Promise<{ filePath: string; fileName: string; fileSize: number; mimeType: string }> {
  const { supabase } = await import('../lib/supabase');

  // Get current user for folder path
  const { data: { user } } = await supabase.auth.getUser();

  const extension = getFileExtension(file.name);
  const fileName = file.name;
  const filePath = user
    ? `${user.id}/files/${objectId}/original.${extension}`
    : `files/${objectId}/original.${extension}`;

  // Store in IndexedDB for offline access
  await storeFileInIndexedDB(objectId, file);

  // Upload to Supabase Storage if user is authenticated
  if (user) {
    try {
      const { error } = await supabase.storage
        .from('pkm-media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.warn('Failed to upload file to Supabase, using local storage only:', error.message);
      } else {
        console.log('✅ File uploaded to Supabase:', filePath);
      }
    } catch (error) {
      console.error('Error uploading file to Supabase:', error);
      // Continue with local storage only
    }
  }

  return {
    filePath,
    fileName,
    fileSize: file.size,
    mimeType: file.type || getMimeTypeFromExtension(extension)
  };
}

/**
 * Store file in IndexedDB
 */
async function storeFileInIndexedDB(objectId: string, file: File): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PKM_Files', 1);

    request.onerror = () => reject(request.error);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('files')) {
        db.createObjectStore('files', { keyPath: 'objectId' });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['files'], 'readwrite');
      const store = transaction.objectStore('files');

      const fileData = {
        objectId,
        file,
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString()
      };

      const putRequest = store.put(fileData);

      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
    };
  });
}

/**
 * Retrieve file from IndexedDB
 */
export async function getFileFromIndexedDB(objectId: string): Promise<File | null> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PKM_Files', 1);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['files'], 'readonly');
      const store = transaction.objectStore('files');
      const getRequest = store.get(objectId);

      getRequest.onsuccess = () => {
        const result = getRequest.result;
        resolve(result ? result.file : null);
      };

      getRequest.onerror = () => reject(getRequest.error);
    };
  });
}

/**
 * Delete file from storage
 */
export async function deleteFileFromVault(objectId: string): Promise<void> {
  // Delete from IndexedDB
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PKM_Files', 1);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['files'], 'readwrite');
      const store = transaction.objectStore('files');
      const deleteRequest = store.delete(objectId);

      deleteRequest.onsuccess = () => resolve();
      deleteRequest.onerror = () => reject(deleteRequest.error);
    };
  });
}

/**
 * Generate thumbnail for image files
 */
export async function generateImageThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Calculate dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > THUMBNAIL_SIZE) {
            height = (height * THUMBNAIL_SIZE) / width;
            width = THUMBNAIL_SIZE;
          }
        } else {
          if (height > THUMBNAIL_SIZE) {
            width = (width * THUMBNAIL_SIZE) / height;
            height = THUMBNAIL_SIZE;
          }
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to generate thumbnail'));
          }
        }, 'image/jpeg', 0.8);
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Store thumbnail in IndexedDB
 */
export async function storeThumbnail(objectId: string, thumbnail: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PKM_Thumbnails', 1);

    request.onerror = () => reject(request.error);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('thumbnails')) {
        db.createObjectStore('thumbnails', { keyPath: 'objectId' });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['thumbnails'], 'readwrite');
      const store = transaction.objectStore('thumbnails');

      const thumbnailData = {
        objectId,
        thumbnail,
        createdAt: new Date().toISOString()
      };

      const putRequest = store.put(thumbnailData);

      putRequest.onsuccess = () => resolve(`thumbnails/${objectId}`);
      putRequest.onerror = () => reject(putRequest.error);
    };
  });
}

/**
 * Get thumbnail from IndexedDB as data URL
 */
export async function getThumbnailDataURL(objectId: string): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PKM_Thumbnails', 1);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['thumbnails'], 'readonly');
      const store = transaction.objectStore('thumbnails');
      const getRequest = store.get(objectId);

      getRequest.onsuccess = () => {
        const result = getRequest.result;
        if (result && result.thumbnail) {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(result.thumbnail);
        } else {
          resolve(null);
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    };
  });
}

/**
 * Download a file from IndexedDB to the user's computer
 */
export async function downloadFile(objectId: string, fileName: string): Promise<void> {
  const file = await getFileFromIndexedDB(objectId);
  if (!file) {
    throw new Error('File not found in storage');
  }

  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
